// A template draft from a generated media item — what "Use in a template" opens the composer with.
//
// For WhatsApp, the generated text has nowhere else to go: a campaign sends the approved template,
// whose body is fixed when it is created. So the media's text becomes the body, its selected photo
// (or video) becomes the header, and the template remembers which media it came from so campaigns
// using it start with that same creative. Everything here is a DEFAULT — the composer leaves every
// field editable, and the server re-validates whatever is submitted.
//
// Pure, with no React and no network, like templateComposer.ts: the page fetches the media, this
// shapes it, and the composer renders the result.

import {
  normalize,
  selectedText,
  allPhotoOptions,
  allVideoOptions,
  type GeneratedContentV2,
} from './generatedContent';
import {
  optOutButton,
  slugifyTemplateName,
  SAMPLE_ACCEPT,
  type TemplateDraft,
  type TemplateMediaAsset,
} from './templateComposer';

export interface MediaForTemplate {
  id: string;
  name: string;
  generatedContent?: unknown;
}

/**
 * The generated text as a template body.
 *
 * Language models write Markdown; WhatsApp has its own, single-character emphasis. Converting is
 * the difference between a reviewer reading "**New launch**" with the asterisks showing and reading
 * it in bold. Headings have no WhatsApp equivalent, so their markers go. Nothing is cut short: a
 * body over WhatsApp's limit is shown as such in the composer, not silently truncated.
 */
export const toTemplateBody = (text: string): string => String(text ?? '')
  .replace(/\r\n?/g, '\n')
  .replace(/\*\*(.+?)\*\*/g, '*$1*')
  .replace(/__(.+?)__/g, '_$1_')
  .replace(/~~(.+?)~~/g, '~$1~')
  .replace(/^#{1,6}\s+/gm, '')
  .replace(/[ \t]+$/gm, '')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

/**
 * 'ar' when the text is mostly Arabic, else 'en'.
 *
 * It decides more than a label: the mandatory opt-out button's text is checked against the
 * template's language, so an Arabic body under 'en' would carry an English opt-out button.
 */
export const detectTemplateLanguage = (text: string): 'ar' | 'en' => {
  const arabic = (String(text ?? '').match(/[؀-ۿݐ-ݿࢠ-ࣿ]/g) || []).length;
  const latin = (String(text ?? '').match(/[A-Za-z]/g) || []).length;
  return arabic > latin ? 'ar' : 'en';
};

/**
 * A valid, unused template name from the media's name.
 *
 * Template names are lowercase Latin only, so an Arabic media name slugifies to nothing — hence the
 * dated fallback. Numbered against the names already loaded, so the first suggestion is not a
 * guaranteed 409; the server still has the final say.
 */
export const defaultTemplateName = (mediaName: string, takenNames: string[] = [], now = new Date()): string => {
  const date = now.toISOString().slice(0, 10).replace(/-/g, '');
  const base = slugifyTemplateName(mediaName).replace(/_+$/, '') || `campaign_${date}`;
  const taken = new Set(takenNames);
  let name = base;
  for (let n = 2; taken.has(name); n += 1) name = `${base}_${n}`;
  return name;
};

const acceptedFor = (kind: 'photo' | 'video') => SAMPLE_ACCEPT[kind === 'photo' ? 'IMAGE' : 'VIDEO'].split(',');

/**
 * The media's photos or videos that could be a template header, the builder's selection first.
 *
 * Mirrors the server's listing (campaignHeaderAssets): only options with a stored object can be
 * sent, so only those are offered. A type WhatsApp refuses is offered but not usable, with the
 * reason — hiding it would leave somebody looking for their picture.
 */
export const mediaHeaderAssets = (gc: GeneratedContentV2, kind: 'photo' | 'video'): TemplateMediaAsset[] => {
  const options = kind === 'photo' ? allPhotoOptions(gc) : allVideoOptions(gc);
  const order = kind === 'photo' ? gc.selection.photoUids : gc.selection.videoUids;
  const accepted = acceptedFor(kind);

  const assets = options
    .filter((option) => Boolean(option.metadata?.objectName))
    .map((option): TemplateMediaAsset => {
      const mime = typeof option.metadata?.mime === 'string' ? option.metadata.mime.toLowerCase() : null;
      // An unrecorded type is given the benefit of the doubt; the server sniffs the bytes anyway.
      const usable = !mime || accepted.includes(mime);
      return {
        uid: option.uid,
        kind,
        label: `${option.genLabel || 'Generation'} — ${option.id}`,
        url: option.url ?? null,
        mime,
        selected: order.includes(option.uid),
        usable,
        reason: usable
          ? undefined
          : `WhatsApp accepts ${accepted.map((m) => m.split('/')[1].toUpperCase()).join(' or ')} only — this is ${mime}.`,
      };
    });

  return assets.sort((a, b) => {
    if (a.selected !== b.selected) return a.selected ? -1 : 1;
    return a.selected ? order.indexOf(a.uid) - order.indexOf(b.uid) : 0;
  });
};

/** The first asset of this kind worth defaulting to: a selected, usable one. */
export const firstHeaderAsset = (assets: TemplateMediaAsset[], kind: 'photo' | 'video') =>
  assets.find((a) => a.kind === kind && a.usable && a.selected) ?? null;

/**
 * The composer's starting draft, and the assets its header picker offers.
 *
 * The header is the first usable SELECTED photo, else the first selected video, else none — the
 * builder's final selection is what the user already said they want. With media headers
 * unavailable on this server (no META_APP_ID), there is no header at all rather than one that
 * cannot be submitted.
 */
export const draftFromMedia = (
  media: MediaForTemplate,
  { canUploadSamples, takenNames = [] }: { canUploadSamples: boolean; takenNames?: string[] },
): { draft: Partial<TemplateDraft>; assets: TemplateMediaAsset[] } => {
  const gc = normalize(media.generatedContent);
  const body = toTemplateBody(selectedText(gc)?.content ?? gc.text ?? '');
  const language = detectTemplateLanguage(body);
  const assets = [...mediaHeaderAssets(gc, 'photo'), ...mediaHeaderAssets(gc, 'video')];

  const header = canUploadSamples
    ? firstHeaderAsset(assets, 'photo') ?? firstHeaderAsset(assets, 'video')
    : null;

  return {
    draft: {
      name: defaultTemplateName(media.name, takenNames),
      language,
      category: 'MARKETING',
      headerType: header ? (header.kind === 'photo' ? 'IMAGE' : 'VIDEO') : 'NONE',
      headerAssetUid: header?.uid ?? null,
      headerPreviewUrl: header?.url ?? null,
      body,
      buttons: [optOutButton(language)],
      campaignMediaId: media.id,
    },
    assets,
  };
};
